/**
 * Utility functions for working with the application's base path
 */

import { env } from "@/lib/env"

// Get the base path from the environment variable
// If it's just "/", treat it as empty string to avoid double slashes
export const basePath = env.basePath === "/" ? "" : env.basePath || ""

/**
 * Prepends the base path to a URL if it doesn't already have it
 * @param url The URL to prepend the base path to
 * @returns The URL with the base path prepended
 */
export function getBasePathUrl(url: string): string {
  // If the URL is absolute or already includes the base path, return it as is
  if (url.startsWith("http") || url.startsWith("//")) {
    return url
  }

  // Check if URL already starts with basePath followed by / or end of string
  if (basePath && (url === basePath || url.startsWith(`${basePath}/`))) {
    return url
  }

  // Make sure the URL starts with a slash
  const normalizedUrl = url.startsWith("/") ? url : `/${url}`

  // If there's no base path, return the URL as is
  if (!basePath) {
    return normalizedUrl
  }

  // Combine the base path and URL
  return `${basePath}${normalizedUrl}`
}

/**
 * Creates a URL with the base path and any additional query parameters
 * @param path The path to create a URL for
 * @param params Optional query parameters
 * @returns The full URL with base path and query parameters
 */
export function createUrl(path: string, params?: Record<string, string>): string {
  const baseUrl = getBasePathUrl(path)

  if (!params || Object.keys(params).length === 0) {
    return baseUrl
  }

  const searchParams = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    searchParams.set(key, value)
  }

  return `${baseUrl}?${searchParams.toString()}`
}

